/**
 * Template picker — the Templates tab lists the LIVE SFMC templates, and only
 * those (2026-08-19, Jack: "remove all the hard coded templates and only pull in
 * the sfmc blocks/template under the template tab").
 *
 * ⚠️ DELIBERATE DIVERGENCE FROM THE MONOLITH — do not "restore parity".
 * The monolith's `mvpTplGrid` (index.html L4891–4917) hard-codes a five-row table
 * of families: label, name-match regex, catalog name, colour and a literal eblast
 * count (234/153/152/135/118, a 2026-07-25 snapshot of a Creative Template
 * Dashboard ranking). The port copied it verbatim. It is deleted here. A template
 * is one `ECH_TPL_*` block in Content Builder, so adding/renaming/retiring one is
 * Layne's edit and this file must never need to change for it.
 *
 * What each card shows now, all of it live:
 *   • THUMBNAIL — the template assembled from its own SFMC blocks and rendered by
 *     the same pure-TS renderer the Studio and the export use (`echPreviewHtml`),
 *     in the favorites' scaled 600px iframe. Not a catalog thumbnail matched by
 *     fuzzy name; the actual email the Studio is about to build.
 *   • NAME — the canonical `template` the definition block declares.
 *   • META — "N blocks", the length of the live placement sequence.
 * There is no eblast count and no family colour, because neither exists on this
 * path. A template that cannot be assembled yet gets NO thumbnail rather than a
 * coloured tile standing in for artwork.
 *
 * Loading / failure are stated on the surface: the block pull is 40–60s cold, and
 * if `/sfmc-blocks` does not answer the tab says so and offers a retry. It never
 * falls back to a list written in this repo.
 *
 * ★ RMT / Division Favorites (2026-08-08) is UNTOUCHED by that change: the
 * monolith's header mode toggle (#tplMode, index.html L1707) and the whole
 * favorites browser (__favMode, L4972–5083) — By Division / By RMT sub-toggle,
 * the 13 RMT cards, drill into an RMT's divisions, then a division's saved emails
 * (live scaled minis via /fav-html), ✕ unsave (/fav-delete), pick-to-preview —
 * stay exactly as they were, including the MVP favorites filter (MVP_JOB_RE).
 *
 * DRAFTS (2026-09-18, pilot item 1 — services/namedDrafts.ts; dev/QA only): a third
 * header mode, rendered only when `onPickDraft` is passed. Jack: "drafts should be
 * set up the same way … division folders / RMT folders" — so it walks the SAME
 * three levels as Favorites (By Division / By RMT → an RMT's divisions → the
 * division's saved work) over the draft ledger (/draft-list), with the same ✕
 * delete chip (/draft-delete). Opening a draft hands it to App, which restores the
 * whole request; a draft that has been submitted shows as such until the eblast is
 * sent and the ledger clears it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { draftDeleteRemote, favDelete, getFavoriteHtml, getFavorites, getNamedDraftHtml, getNamedDrafts, type Favorite } from "../services/api";
import { namedDraftAge, type NamedDraft } from "../services/namedDrafts";
import { mvpFavAllow, mvpFavContentAllow } from "../services/mvpMode";
import { echTplFor } from "../services/echTemplates";
import { echBlockCount, echPreviewHtml, type EchListStatus } from "../services/useEchTemplates";

interface Props {
  /** canonical names of the live SFMC templates, in the order SFMC returned them */
  names: string[];
  /** the block pull's state — drives the loading / unreachable copy on the tab */
  status: EchListStatus;
  /** re-run the block pull after a failure */
  onRetry: () => void;
  /** bumped when the library reloads, so assembled previews re-derive */
  version: number;
  current: string;
  /** Step-1 division code — drives "Your division" / "RMT n · yours" ordering (monolith $("divisionCode").value) */
  div: string;
  onPick: (name: string) => void;
  /** a favorite was picked — the monolith closes the modal, shows the saved HTML in the
   *  preview and loads it into the Creative Studio as "★ name" (index.html L5072–5079) */
  onPickFavorite: (name: string, html: string) => void;
  /** a NAMED draft was picked — App restores the stored request. Absent = no Drafts mode. */
  onPickDraft?: (d: NamedDraft) => void;
  onClose: () => void;
  /** bottom-center toast (monolith __fvToast) */
  notify?: (m: string) => void;
}

/* RMT ↔ division mapping — verbatim from the monolith (index.html L4973–4974;
   locked, from rmt-deck-pipeline/dashboard-scrape-recipe.md, codes from
   Division_Level_Content. 2026-07-23). */
const RMT_OF: Record<string, number> = { NAL: 1, ATL: 1, GFC: 1, NSH: 1, GAL: 2, CRS: 2, GVL: 2, SUN: 2, MDH: 3, CJH: 3, PEN: 3, CHB: 4, IND: 4, MIN: 4, JAX: 5, MFL: 5, PBH: 5, DAH: 5, BOI: 6, POR: 6, SEA: 6, BAH: 7, BAU: 7, NEV: 7, REN: 7, LSC: 7, OCH: 8, FNO: 8, INL: 8, SDG: 8, COS: 9, AZH: 9, UTH: 9, SAN: 10, DFW: 10, HOU: 10, OLH: 11, TPU: 11, BEN: 12, KAN: 12, LRA: 12, OKC: 12, TLA: 12, LEH: 13 };
/* card copy per mode — favorites keep the monolith's line word for word */
const FAV_WORD = { one: "saved email", many: "saved emails", tail: " — reusable and editable.", icon: "★ " };
const DRAFT_WORD = { one: "draft", many: "drafts", tail: " — open to keep going.", icon: "" };
const RMT_LEAD: Record<number, string> = { 1: "Tucker", 2: "Dooley", 3: "Bock", 4: "Austin", 5: "Gonzalez", 6: "Siegler", 7: "Price", 8: "Price", 9: "Castilian", 10: "Olson", 11: "Ward", 12: "Underdown", 13: "Shenk" };

/* the monolith's modalMode / __favView / __favRmtSel / __favDivSel and __favCache are
   MODULE-level there (index.html L4840/L4936/L4975), so mode, drill position and fetched
   data survive close/reopen — mirrored here as module state feeding useState initials. */
let lastMode: "regular" | "favorites" | "drafts" = "regular";
let lastFavView: "division" | "rmt" = "division";
let lastRmtSel: number | null = null;
let lastDivSel: string | null = null;
let favListCache: { t: number; items: Favorite[] } | null = null; /* __favCache.__all (60s TTL) */
const favHtmlCache: Record<string, string> = {}; /* __favCache.h (session-lived) */
let draftListCache: { t: number; items: NamedDraft[] } | null = null; /* same 60s TTL as favorites */
const draftHtmlCache: Record<string, string> = {};

/** GET /draft-html — the draft's rendered HTML for its mini, cached like favorites */
async function draftHtml(id: string): Promise<string> {
  if (draftHtmlCache[id]) return draftHtmlCache[id];
  const t = await getNamedDraftHtml(id).catch(() => "");
  if (t) draftHtmlCache[id] = t;
  return t;
}
/** one row shape for the shared folder tree — a favorite or a draft */
interface TreeRow { key: string; div: string; kind: "fav" | "draft" }

/** GET /fav-html — cached like the monolith's __favHtmlFor (index.html L4956–4963) */
async function favHtml(key: string): Promise<string> {
  if (favHtmlCache[key]) return favHtmlCache[key];
  const t = await getFavoriteHtml(key).catch(() => "");
  if (t) favHtmlCache[key] = t;
  return t;
}

/* ── MVP CONTENT VERDICTS (Jack 2026-08-21: "can only save/show templates related
   to the MVP set up — not these") ──────────────────────────────────────────────
   The name regex let two old test saves through because they were literally NAMED
   "…Coming Soon…" while their saved HTML was a webinar email and a rates email.
   A Studio-build favorite is now shown only if its saved HTML carries the ECH
   manifest stamp — the build's own provenance. Verdicts persist in localStorage
   keyed key+updated, so a 1.9MB legacy save is fetched once ever; a re-saved
   favorite (new `updated`) is re-checked. */
const favVerdicts: Record<string, boolean> = (() => {
  try { return JSON.parse(localStorage.getItem("lsr_favMvp") || "{}") as Record<string, boolean>; } catch { return {}; }
})();
const verdictKey = (f: Favorite) => f.key + "@" + f.updated;
async function favMvpVerdict(f: Favorite): Promise<boolean> {
  const vk = verdictKey(f);
  if (vk in favVerdicts) return favVerdicts[vk];
  const ok = mvpFavContentAllow(await favHtml(f.key));
  favVerdicts[vk] = ok;
  try { localStorage.setItem("lsr_favMvp", JSON.stringify(favVerdicts)); } catch { /* quota */ }
  return ok;
}

/* Live scaled render of the template's real HTML — the favorites' __favMiniInto
   pattern (index.html L4988): 600px-wide iframe, scale = thumbWidth/600,
   height covers the thumb so the email's top fills it (object-position: top). */
function MiniPreview({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [dim, setDim] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const m = () => { const w = el.clientWidth, h = el.clientHeight; if (w && h) setDim({ w, h }); };
    m();
    const ro = new ResizeObserver(m);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const sc = dim ? dim.w / 600 : 0;
  return (
    <div className="gtpl-thumb" ref={ref}>
      {dim && sc > 0 && (
        <iframe className="tpl-mini" title="Template preview" tabIndex={-1} sandbox="" scrolling="no"
          srcDoc={html} width={600} height={Math.ceil(dim.h / sc)}
          style={{ transform: `scale(${sc})` }} />
      )}
    </div>
  );
}

/* A saved email's live mini — __favMiniInto (index.html L4964–4971): fetch the
   favorite's HTML (cached), then the same 600px-iframe scale-to-box render.
   `cls` is the container class: "cc" on division/RMT card covers, "gtpl-thumb"
   on the saved-email cards. */
function FavMini({ favKey, cls, kind = "fav" }: { favKey: string; cls: string; kind?: "fav" | "draft" }) {
  const ref = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string>((kind === "draft" ? draftHtmlCache : favHtmlCache)[favKey] || "");
  const [dim, setDim] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const m = () => { const w = el.clientWidth, h = el.clientHeight; if (w && h) setDim({ w, h }); };
    m();
    const ro = new ResizeObserver(m);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (html) return;
    let dead = false;
    (kind === "draft" ? draftHtml(favKey) : favHtml(favKey)).then((h) => { if (!dead && h) setHtml(h); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favKey, kind]);
  const sc = dim ? dim.w / 600 : 0;
  return (
    <div className={cls} ref={ref}>
      {html && dim && sc > 0 && (
        <iframe className="fav-mini" title="Saved email preview" tabIndex={-1} sandbox="" scrolling="no"
          srcDoc={html} width={600} height={Math.ceil(dim.h / sc)}
          style={{ transform: `scale(${sc})` }} />
      )}
    </div>
  );
}

export default function MvpTemplateGallery({ names, status, onRetry, version, current, div, onPick, onPickFavorite, onPickDraft, onClose, notify }: Props) {
  const [q, setQ] = useState("");

  /* ── favorites-mode state (monolith modalMode + __favView/__favRmtSel/__favDivSel) ── */
  const [mode, setModeS] = useState<"regular" | "favorites" | "drafts">(onPickDraft ? lastMode : (lastMode === "drafts" ? "regular" : lastMode));
  const [favView, setFavViewS] = useState<"division" | "rmt">(lastFavView);
  const [rmtSel, setRmtSelS] = useState<number | null>(lastRmtSel);
  const [divSel, setDivSelS] = useState<string | null>(lastDivSel);
  const setMode = (m: "regular" | "favorites" | "drafts") => { lastMode = m; setModeS(m); };
  const setFavView = (v: "division" | "rmt") => { lastFavView = v; setFavViewS(v); };
  const setRmtSel = (r: number | null) => { lastRmtSel = r; setRmtSelS(r); };
  const setDivSel = (d: string | null) => { lastDivSel = d; setDivSelS(d); };
  /* null = loading; favErr with no cache = the /fav-list ledger is unreachable */
  const [favs, setFavs] = useState<Favorite[] | null>(favListCache ? favListCache.items : null);
  const [favErr, setFavErr] = useState(false);

  /* __favAll (index.html L4944–4955): /fav-list, 60s cache, force on unsave */
  const loadFavs = (force = false) => {
    const now = Date.now();
    if (!force && favListCache && now - favListCache.t < 60000) { setFavs(favListCache.items); setFavErr(false); return; }
    getFavorites()
      .then((d) => {
        const items = (d && d.ok && d.items) || [];
        favListCache = { t: Date.now(), items };
        setFavs(items); setFavErr(false);
      })
      .catch(() => { setFavErr(true); }); /* offline → note in the body, never fake rows */
  };
  useEffect(() => { if (mode === "favorites") loadFavs(); }, [mode]);

  /* drafts ledger — /draft-list, same 60s cache, force after a delete */
  const [drafts, setDrafts] = useState<NamedDraft[] | null>(draftListCache ? draftListCache.items : null);
  const [draftErr, setDraftErr] = useState(false);
  const loadDrafts = (force = false) => {
    const now = Date.now();
    if (!force && draftListCache && now - draftListCache.t < 60000) { setDrafts(draftListCache.items); setDraftErr(false); return; }
    getNamedDrafts()
      .then((d) => {
        const items = (d && d.ok && d.items) || [];
        draftListCache = { t: Date.now(), items };
        setDrafts(items); setDraftErr(false);
      })
      .catch(() => { setDraftErr(true); });
  };
  useEffect(() => { if (mode === "drafts") loadDrafts(); }, [mode]);

  /* One card per LIVE template, assembled from Layne's own blocks. Synchronous —
     `echDoc` reads the registry the pull already filled, so there is no second
     round-trip and no per-card loading state; `version` re-derives them if the
     library reloads on fresher markup. */
  const cards = useMemo(
    () => names.map((nm) => ({ name: nm, html: echPreviewHtml(nm), blocks: echBlockCount(nm) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [names, version],
  );

  const query = q.trim().toLowerCase();
  const shown = cards.filter((c) => !query || c.name.toLowerCase().indexOf(query) > -1);

  /* monolith fitGrid(grid, n) (index.html L4871): cols = n (≤6), per-card max width */
  const gridStyleFor = (n: number) => {
    const cols = Math.max(1, Math.min(6, n));
    const cardMax = n <= 1 ? 560 : n === 2 ? 470 : n === 3 ? 400 : n <= 5 ? 330 : 300;
    return n
      ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, maxWidth: cols * cardMax + (cols - 1) * 16, margin: "0 auto", width: "100%" }
      : undefined;
  };

  /* ── the Templates tab — LIVE SFMC only ────────────────────────────────────
     Four states, and none of them is a hard-coded list. A failed pull SAYS it
     failed; it does not quietly show templates written in this repo. */
  const tplBody = () => {
    if (status === "error" && !names.length) {
      return (
        <div className="gal-note">
          Templates couldn't load — the live SFMC block library (/sfmc-blocks) is unreachable right now.{" "}
          <button className="rb-gal-back" style={{ margin: "10px auto 0", display: "block" }} onClick={onRetry}>Retry</button>
        </div>
      );
    }
    if (status === "loading" && !names.length) {
      return <div className="gal-note">Loading templates from SFMC… the first pull of the block library takes up to a minute.</div>;
    }
    if (!names.length) {
      return <div className="gal-note">No templates in SFMC — the block library has no ECH_TPL_* template blocks.</div>;
    }
    if (!shown.length) return <div className="gal-note">No matching templates.</div>;
    return (
      <div className="gtpl-grid" style={gridStyleFor(shown.length)}>
        {shown.map((c) => (
          <button key={c.name} type="button"
            className={"gtpl-card" + (echTplFor(current) === c.name ? " sel" : "")}
            onClick={() => onPick(c.name)}>
            {/* the assembled template itself. NO tile fallback: a template we cannot
                render yet shows no thumbnail rather than a coloured stand-in. */}
            {c.html ? <MiniPreview html={c.html} /> : null}
            <div className="gtpl-nm">{c.name}</div>
            <div className="mt">{c.blocks} block{c.blocks === 1 ? "" : "s"}</div>
          </button>
        ))}
      </div>
    );
  };

  /* ── favorites derivations (monolith __favMode, index.html L4989–5083) ── */
  const cur = (div || "").toUpperCase();
  const curRmt = RMT_OF[cur] || 0;
  /* content verdicts land async (one /fav-html fetch per unverified row, then cached) */
  const [favOk, setFavOk] = useState<Record<string, boolean>>({});
  useEffect(() => {
    let dead = false;
    (favs || []).forEach((f) => {
      if (f.source === "Past job") return;             /* name-gated at save by mvpJobAllow */
      const vk = verdictKey(f);
      if (vk in favVerdicts) return;                   /* known — read synchronously below */
      favMvpVerdict(f).then((ok) => { if (!dead) setFavOk((s) => ({ ...s, [vk]: ok })); });
    });
    return () => { dead = true; };
  }, [favs]);
  /* MVP favorites: a Past-job row keeps the 5-template NAME gate (its content is a
     sent legacy email by definition); a Studio-build row must PROVE it — the ECH
     manifest in its saved HTML (Jack 2026-08-21: name ≠ build). */
  const favItems = useMemo(() => (favs || []).filter((f) => (
    f.source === "Past job"
      ? mvpFavAllow(f.name)
      : (favVerdicts[verdictKey(f)] ?? favOk[verdictKey(f)]) === true
  )), [favs, favOk]);

  const favRows: (TreeRow & { fav: Favorite })[] = useMemo(() => favItems.map((f) => ({ key: f.key, div: f.div, kind: "fav" as const, fav: f })), [favItems]);

  const pickFav = async (f: Favorite) => {
    const h = await favHtml(f.key);
    if (!h) { notify?.("Couldn't load that favorite."); return; }
    onPickFavorite(f.name, h); /* close + preview + Studio load + toast live in FormView */
  };
  const removeFav = async (f: Favorite) => {
    if (!window.confirm(`Remove "${f.name}" from ${divSel} favorites?`)) return;
    notify?.("Removing…");
    const d = await favDelete(f.id).catch(() => null);
    if (d && d.ok) { notify?.("Removed."); loadFavs(true); }
    else notify?.("Remove failed.");
  };

  /* By Division / By RMT segmented sub-toggle — __favToggleHtml (index.html L4976–4977) */
  const favTop = (
    <div className="favtop">
      <div className="favview-seg">
        <button type="button" className={favView === "division" ? "on" : ""} onClick={() => { setFavView("division"); setRmtSel(null); }}>By Division</button>
        <button type="button" className={favView === "rmt" ? "on" : ""} onClick={() => { setFavView("rmt"); setRmtSel(null); }}>By RMT</button>
      </div>
    </div>
  );

  /* division cards — __favDivCardsHtml (index.html L4978–4984) */
  const divCards = (codes: string[], byDiv: Record<string, TreeRow[]>, word: { one: string; many: string; tail: string; icon: string } = FAV_WORD) => (
    <div className="cat-grid">
      {codes.map((c) => {
        const cnt = (byDiv[c] || []).length;
        const same = !!(curRmt && RMT_OF[c] === curRmt && c !== cur);
        const col = c === cur ? "#C4961A" : same ? "#2f6f6f" : "#1B365D";
        return (
          <button key={c} type="button" className="cat-card" style={{ ["--c" as string]: col }} onClick={() => setDivSel(c)}>
            <h4>{word.icon}{c}{c === cur ? <span className="cat-html">Your division</span> : same ? <span className="cat-html">RMT {curRmt}</span> : null}</h4>
            <p>{cnt} {cnt === 1 ? word.one : word.many}{word.tail}</p>
            <div className="cat-cover">
              {(byDiv[c] || []).slice(0, 4).map((f) => <FavMini key={f.key} favKey={f.key} cls="cc" kind={f.kind} />)}
            </div>
          </button>
        );
      })}
    </div>
  );

  /* the 13 RMT cards — __favMode By RMT (shared by Favorites and Drafts) */
  const rmtCards = (byDiv: Record<string, TreeRow[]>, word: { one: string; many: string }) => {
    const byRmt: Record<number, string[]> = {};
    Object.keys(byDiv).forEach((c) => { const r = RMT_OF[c] || 0; if (r) (byRmt[r] = byRmt[r] || []).push(c); });
    const rmts: number[] = []; for (let i = 1; i <= 13; i++) rmts.push(i);
    rmts.sort((a, b) => (a === curRmt ? -1 : b === curRmt ? 1 : a - b));
    return (
      <div className="cat-grid">
        {rmts.map((r) => {
          const divCodes = byRmt[r] || [];
          const count = divCodes.reduce((s, c) => s + (byDiv[c] || []).length, 0);
          const covers: TreeRow[] = [];
          divCodes.forEach((c) => (byDiv[c] || []).forEach((f) => covers.push(f)));
          return (
            <button key={r} type="button" className="cat-card" style={{ ["--c" as string]: r === curRmt ? "#C4961A" : "#1B365D" }} onClick={() => setRmtSel(r)}>
              <h4>RMT {r}<span className="cat-html">{RMT_LEAD[r] || ""}{r === curRmt ? " · yours" : ""}</span></h4>
              <p>{count} {count === 1 ? word.one : word.many} · {divCodes.length} division{divCodes.length === 1 ? "" : "s"}.</p>
              <div className="cat-cover">
                {covers.slice(0, 4).map((k) => <FavMini key={k.key} favKey={k.key} cls="cc" kind={k.kind} />)}
              </div>
            </button>
          );
        })}
      </div>
    );
  };
  /* division-card ordering — your division first, then its RMT-siblings, then by count */
  const orderCodes = (codes: string[], byDiv: Record<string, TreeRow[]>) => codes.sort((a, b) => {
    if (a === cur) return -1; if (b === cur) return 1;
    const ar = curRmt && RMT_OF[a] === curRmt ? 0 : 1, br = curRmt && RMT_OF[b] === curRmt ? 0 : 1;
    if (ar !== br) return ar - br;
    return (byDiv[b] || []).length - (byDiv[a] || []).length || a.localeCompare(b);
  });

  /* the favorites body — the three levels of __favMode, in the monolith's order */
  const favBody = () => {
    if (favErr && !favs) {
      /* live endpoint unreachable — say so; NEVER fabricate rows */
      return (
        <div className="gal-note">
          Favorites couldn't load — the live ledger (/fav-list) is unreachable right now.{" "}
          <button className="rb-gal-back" style={{ margin: "10px auto 0", display: "block" }} onClick={() => { setFavErr(false); loadFavs(true); }}>Retry</button>
        </div>
      );
    }

    /* Level 2 — an RMT's divisions (drilled in from By RMT) */
    if (!divSel && favView === "rmt" && rmtSel) {
      if (!favs) return <div className="gal-note">Loading…</div>;
      const byDiv: Record<string, TreeRow[]> = {};
      favRows.forEach((f) => { if ((RMT_OF[f.div] || 0) === rmtSel) (byDiv[f.div] = byDiv[f.div] || []).push(f); });
      const codes = Object.keys(byDiv).sort((a, b) => (a === cur ? -1 : b === cur ? 1 : (byDiv[b] || []).length - (byDiv[a] || []).length || a.localeCompare(b)));
      if (!codes.length) return <div className="gal-note">No saved emails in RMT {rmtSel} yet.</div>;
      return divCards(codes, byDiv);
    }

    /* Level 1 — top: sub-toggle + division cards OR the 13 RMT cards */
    if (!divSel) {
      if (!favs) return <div className="gal-note">Loading favorites…</div>;
      const byDiv: Record<string, TreeRow[]> = {};
      favRows.forEach((f) => { const dv = f.div || "—"; (byDiv[dv] = byDiv[dv] || []).push(f); });

      if (favView === "rmt") return <>{favTop}{rmtCards(byDiv, { one: "saved email", many: "saved emails" })}</>;

      /* division view — your division first, then its RMT-siblings, then the rest */
      const codes = Object.keys(byDiv);
      if (cur && codes.indexOf(cur) < 0) codes.push(cur);
      orderCodes(codes, byDiv);
      if (!codes.length) return <>{favTop}<div className="gal-note">No favorites saved yet — star a past job or use ★ Save favorite in Design Email.</div></>;
      return <>{favTop}{divCards(codes, byDiv)}</>;
    }

    /* Level 3 — a specific division's favorites */
    if (!favs) return <div className="gal-note">Loading {divSel} favorites…</div>;
    const mine = favItems.filter((f) => (f.div || "") === divSel && (!query || String(f.name).toLowerCase().indexOf(query) > -1));
    if (!mine.length) return <div className="gal-note">Nothing saved for {divSel}{query ? ` matching "${q.trim()}"` : ""} yet.</div>;
    return (
      <div className="gtpl-grid" style={gridStyleFor(mine.length)}>
        {mine.map((f) => (
          <button key={f.id} type="button" className="gtpl-card fav-tplcard" onClick={() => pickFav(f)}>
            <span className="fav-x" title="Remove from favorites" onClick={(e) => { e.stopPropagation(); removeFav(f); }}>×</span>
            <FavMini favKey={f.key} cls="gtpl-thumb" />
            <div className="gtpl-nm">{f.name}</div>
            <div className="mt">
              <span className="dot active" />v{f.versions || 1}{f.updated ? " · " + String(f.updated).slice(0, 10) : ""}
            </div>
          </button>
        ))}
      </div>
    );
  };

  /* ── DRAFTS (2026-09-18) — the same three levels over the draft ledger ────────── */
  const removeDraft = async (d: NamedDraft) => {
    if (!window.confirm(`Delete the draft "${d.name}"? This can't be undone.`)) return;
    notify?.("Deleting…");
    const r = await draftDeleteRemote(d.id).catch(() => null);
    if (r && r.ok) { notify?.("Draft deleted."); loadDrafts(true); }
    else notify?.("Delete failed.");
  };
  const draftRows: (TreeRow & { draft: NamedDraft })[] = useMemo(() => (drafts || []).map((d) => ({ key: d.id, div: d.div, kind: "draft" as const, draft: d })), [drafts]);
  const draftBody = () => {
    if (draftErr && !drafts) {
      return (
        <div className="gal-note">
          Drafts couldn't load — the draft ledger (/draft-list) is unreachable right now.{" "}
          <button className="rb-gal-back" style={{ margin: "10px auto 0", display: "block" }} onClick={() => { setDraftErr(false); loadDrafts(true); }}>Retry</button>
        </div>
      );
    }
    const word = { one: "draft", many: "drafts" };
    /* Level 2 — an RMT's divisions */
    if (!divSel && favView === "rmt" && rmtSel) {
      if (!drafts) return <div className="gal-note">Loading…</div>;
      const byDiv: Record<string, TreeRow[]> = {};
      draftRows.forEach((f) => { if ((RMT_OF[f.div] || 0) === rmtSel) (byDiv[f.div] = byDiv[f.div] || []).push(f); });
      const codes = Object.keys(byDiv).sort((a, b) => (a === cur ? -1 : b === cur ? 1 : (byDiv[b] || []).length - (byDiv[a] || []).length || a.localeCompare(b)));
      if (!codes.length) return <div className="gal-note">No drafts in RMT {rmtSel} yet.</div>;
      return divCards(codes, byDiv, DRAFT_WORD);
    }
    /* Level 1 — sub-toggle + division cards OR the 13 RMT cards */
    if (!divSel) {
      if (!drafts) return <div className="gal-note">Loading drafts…</div>;
      const byDiv: Record<string, TreeRow[]> = {};
      draftRows.forEach((f) => { const dv = f.div || "—"; (byDiv[dv] = byDiv[dv] || []).push(f); });
      if (favView === "rmt") return <>{favTop}{rmtCards(byDiv, word)}</>;
      const codes = Object.keys(byDiv);
      if (cur && codes.indexOf(cur) < 0) codes.push(cur);
      orderCodes(codes, byDiv);
      if (!codes.length) return <>{favTop}<div className="gal-note">No drafts yet — use 💾 Save as draft in Design Email.</div></>;
      return <>{favTop}{divCards(codes, byDiv, DRAFT_WORD)}</>;
    }
    /* Level 3 — a division's drafts */
    if (!drafts) return <div className="gal-note">Loading {divSel} drafts…</div>;
    const mine = draftRows.filter((r) => (r.div || "") === divSel && (!query || String(r.draft.name).toLowerCase().indexOf(query) > -1));
    if (!mine.length) return <div className="gal-note">No drafts for {divSel}{query ? ` matching "${q.trim()}"` : ""} yet.</div>;
    return (
      <div className="gtpl-grid" style={gridStyleFor(mine.length)} data-testid="draft-grid">
        {mine.map(({ draft: d }) => (
          <button key={d.id} type="button" className="gtpl-card fav-tplcard draft-tplcard" onClick={() => onPickDraft?.(d)}>
            <span className="fav-x" title="Delete draft" onClick={(e) => { e.stopPropagation(); removeDraft(d); }}>×</span>
            <FavMini favKey={d.id} cls="gtpl-thumb" kind="draft" />
            <div className="gtpl-nm">{d.name}</div>
            <div className="mt">
              <span className={"dot" + (d.submittedAt ? "" : " active")} />
              {d.submittedAt ? "Submitted" : "Draft"}{d.savedAt ? " · saved " + namedDraftAge(d.savedAt) : ""}{d.who ? " · " + d.who.replace(/@.*$/, "") : ""}
            </div>
          </button>
        ))}
      </div>
    );
  };

  /* header sub-label — the monolith writes #tplSub per state (L4924/L4994/L5017/L5034/L5050) */
  const sub = mode === "favorites"
    ? (divSel ? `${divSel} division favorites`
      : favView === "rmt" && rmtSel ? `RMT ${rmtSel} · ${RMT_LEAD[rmtSel] || ""} — divisions`
        : favView === "rmt" ? "Saved emails by RMT — pick a team"
          : "Saved emails by division — pick a division")
    : mode === "drafts"
      ? (divSel ? `${divSel} drafts — open one to keep going`
        : favView === "rmt" && rmtSel ? `RMT ${rmtSel} · ${RMT_LEAD[rmtSel] || ""} — divisions`
          : favView === "rmt" ? "Drafts by RMT — pick a team"
            : "Drafts by division — pick a division")
    : status === "ready"
      ? `${names.length} live SFMC template${names.length === 1 ? "" : "s"}`
      : "Live SFMC templates";
  /* #tplBack shows while drilled into an RMT or a division (monolith L5112) */
  const backVisible = (mode === "favorites" || mode === "drafts") && (divSel !== null || rmtSel !== null);

  return (
    <div className="rb-gal-ovl" onClick={onClose}>
      <div className="rb-gal" onClick={(e) => e.stopPropagation()}>
        <div className="rb-gal-hd">
          <div className="rb-gal-tt">
            <b>Choose a template</b>
            <span>{sub}</span>
          </div>
          {/* the monolith's #tplMode header toggle (index.html L1707) — present in MVP too */}
          <div className="rb-gal-mode">
            <button className={mode === "regular" ? "on" : ""} onClick={() => setMode("regular")}>Templates</button>
            <button className={mode === "favorites" ? "on" : ""} onClick={() => setMode("favorites")}>★ RMT / Division Favorites</button>
            {onPickDraft && <button className={mode === "drafts" ? "on" : ""} data-testid="gal-mode-drafts" onClick={() => setMode("drafts")}>Drafts</button>}
          </div>
          <input className="rb-gal-search" placeholder="Search templates…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="rb-gal-x" onClick={onClose}>✕</button>
        </div>
        <div className="rb-gal-bd">
          {backVisible && (
            <button className="rb-gal-back" onClick={() => { if (divSel) setDivSel(null); else setRmtSel(null); }}>← All categories</button>
          )}
          {mode === "favorites" ? favBody() : mode === "drafts" && onPickDraft ? draftBody() : tplBody()}
        </div>
      </div>
    </div>
  );
}
